/**
 * SelectShipmentsForConsolidationDialog — Sprint P0 Warehouse Receipt +
 * Consolidation Completion (27 Feb 2026).
 *
 * Vervangt de scroll-naar-input bug: gebruiker kan nu zoekend, filterend en
 * multi-select shipments toevoegen aan een consolidatie via een moderne
 * enterprise-picker. De bestaande WChipInput blijft naast dit dialog staan
 * voor snelle scan-flows.
 *
 * Eligibility (P0.11):
 *  - Consolidation.transport_mode moet overeenkomen met shipment.transport_mode
 *    (SEA↔SEA, AIR↔AIR, GROUND↔GROUND, COURIER↔COURIER).
 *  - ShopDrop&Ship Air maps naar AIR, ShopDrop&Ship Sea naar SEA.
 *  - Shipments die al aan een actieve consolidatie zijn gekoppeld zijn niet
 *    eligible (backend blokkeert dit sowieso; UI toont het proactief).
 */
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { Checkbox } from "../../ui/checkbox";
import { Loader2, Search, Ship, Plane, Truck, Package, AlertTriangle, CheckCircle2 } from "lucide-react";
import { adminApi, formatApiError } from "../../../context/AuthContext";

const SERVICE_TO_MODE = {
  airfreight: "air",
  seafreight: "sea",
  ground: "ground",
  courier: "courier",
  shopdrop_ship_air: "air",
  shopdrop_ship_sea: "sea",
  shopdrop_ship: "ground",
  counter_sale: "ground",
};

const MODE_LABEL = {
  air: "AIR",
  sea: "SEA",
  ground: "GROUND",
  courier: "COURIER",
};

const MODE_ICON = { air: Plane, sea: Ship, ground: Truck, courier: Package };

function consolidationTransportMode(cons) {
  if (!cons) return null;
  if (cons.type === "courier") return "courier";
  if (cons.type === "awr_awb") return "air";
  const m = (cons.measurement || "").toLowerCase();
  if (m.startsWith("aero")) return "air";
  if (m.startsWith("maritimo")) return "sea";
  if (m === "by_ground") return "ground";
  return null;
}

function checkEligibility(shipment, consolidationMode, alreadyLinkedIds) {
  const reasons = [];
  const shipMode = SERVICE_TO_MODE[shipment.service_type] || null;
  if (!consolidationMode) reasons.push("Consolidatiemodus onduidelijk");
  if (!shipMode) reasons.push("Transportmodus zending onduidelijk");
  if (shipMode && consolidationMode && shipMode !== consolidationMode) {
    reasons.push(`Transportmodus mismatch: zending=${MODE_LABEL[shipMode]}, consolidatie=${MODE_LABEL[consolidationMode]}`);
  }
  const st = (shipment.status || "").toLowerCase();
  if (st === "geannuleerd") reasons.push("Zending is geannuleerd");
  if (alreadyLinkedIds.includes(shipment.id)) {
    reasons.push("Reeds gekoppeld aan deze consolidatie");
  }
  return { eligible: reasons.length === 0, reasons, shipMode };
}

export default function SelectShipmentsForConsolidationDialog({
  open, onOpenChange, consolidation, alreadyLinkedIds = [], onAdded,
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showEligibleOnly, setShowEligibleOnly] = useState(true);

  const consolidationMode = useMemo(
    () => consolidationTransportMode(consolidation),
    [consolidation],
  );
  const ModeIcon = MODE_ICON[consolidationMode] || Package;

  const fetchShipments = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/shipping-labels", {
        params: {
          q: query || undefined,
          limit: 50,
          status_not: "geannuleerd",
        },
      });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      toast.error(formatApiError(e) || "Zendingen laden mislukt");
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    if (open) {
      fetchShipments();
      setSelected({});
    }
  }, [open, fetchShipments]);

  const enrichedItems = useMemo(() => {
    return items.map((s) => ({
      ...s,
      _eligibility: checkEligibility(s, consolidationMode, alreadyLinkedIds),
    }));
  }, [items, consolidationMode, alreadyLinkedIds]);

  const visibleItems = useMemo(() => {
    return showEligibleOnly
      ? enrichedItems.filter((s) => s._eligibility.eligible)
      : enrichedItems;
  }, [enrichedItems, showEligibleOnly]);

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const totals = useMemo(() => {
    let weight = 0;
    let cbm = 0;
    let colli = 0;
    for (const s of enrichedItems) {
      if (!selected[s.id]) continue;
      const sd = s.shipment_details || {};
      weight += Number(sd.weight_kg) || 0;
      cbm += Number(sd.cbm) || 0;
      colli += Number(sd.piece_count) || 0;
    }
    return { weight, cbm, colli };
  }, [selected, enrichedItems]);

  const toggleAll = () => {
    const eligibleIds = visibleItems.filter((s) => s._eligibility.eligible).map((s) => s.id);
    const allSelected = eligibleIds.every((id) => selected[id]);
    const next = { ...selected };
    for (const id of eligibleIds) next[id] = !allSelected;
    setSelected(next);
  };

  const handleSubmit = async () => {
    const chosenShipments = enrichedItems.filter(
      (s) => selected[s.id] && s._eligibility.eligible,
    );
    if (chosenShipments.length === 0) {
      toast.error("Selecteer eerst één of meer eligible zendingen");
      return;
    }
    setSubmitting(true);
    try {
      const wNumbers = chosenShipments.map((s) => s.tracking_number);
      if (new Set(wNumbers).size !== wNumbers.length) {
        toast.error("Kies bij gelijke W-nummers één dossier per keer.");
        return;
      }
      const r = await adminApi.post(
        `/admin/consolidations/${consolidation.id}/shipments`,
        { w_numbers: wNumbers, shipment_choices: Object.fromEntries(chosenShipments.map(s => [s.tracking_number, s.id])) },
      );
      const added = r.data?.added?.length || 0;
      const errors = r.data?.errors?.length || 0;
      if (added) toast.success(`${added} zending${added === 1 ? "" : "en"} toegevoegd`);
      if (errors) toast.warning(`${errors} zending${errors === 1 ? "" : "en"} overgeslagen`);
      onAdded?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e) || "Toevoegen mislukt");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="select-shipments-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-lg font-black text-[#082c50] font-chivo flex items-center gap-2">
            Zendingen toevoegen aan consolidatie
            <Badge variant="outline" className="ml-2">
              <ModeIcon className="w-3 h-3 mr-1" />
              {MODE_LABEL[consolidationMode] || "?"}
            </Badge>
            <span className="font-mono text-sm text-gray-500 font-normal">
              {consolidation?.consolidation_number}
            </span>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Zoek en selecteer eligible zendingen die aan deze consolidatie
            gekoppeld kunnen worden. Alleen zendingen met dezelfde transport-
            modus zijn selecteerbaar.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 border-b border-gray-200 pb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Zoek op W-nummer, klant, ontvanger, bestemming…"
              className="pl-8 text-xs"
              data-testid="select-shipments-search"
              onKeyDown={(e) => {
                if (e.key === "Enter") fetchShipments();
              }}
            />
          </div>
          <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer"
                  data-testid="filter-eligible-only-wrapper">
            <Checkbox
              checked={showEligibleOnly}
              onCheckedChange={(v) => setShowEligibleOnly(!!v)}
              data-testid="filter-eligible-only"
            />
            Alleen eligible
          </label>
          <Button size="sm" variant="outline" onClick={fetchShipments}
                   disabled={loading}
                   className="rounded-none text-xs"
                   data-testid="select-shipments-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Zoeken"}
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <table className="w-full text-xs" data-testid="select-shipments-table">
            <thead className="sticky top-0 bg-white border-b border-gray-200 z-10">
              <tr>
                <th className="w-8 py-2">
                  <Checkbox onCheckedChange={toggleAll}
                             checked={visibleItems.length > 0 &&
                                       visibleItems.filter((s) => s._eligibility.eligible)
                                         .every((s) => selected[s.id])}
                             data-testid="select-shipments-toggle-all" />
                </th>
                <th className="text-left px-2 py-2 font-bold uppercase text-[10px] tracking-wide">W-nr</th>
                <th className="text-left px-2 py-2 font-bold uppercase text-[10px] tracking-wide">Klant</th>
                <th className="text-left px-2 py-2 font-bold uppercase text-[10px] tracking-wide">Ontvanger</th>
                <th className="text-left px-2 py-2 font-bold uppercase text-[10px] tracking-wide">Bestemming</th>
                <th className="text-left px-2 py-2 font-bold uppercase text-[10px] tracking-wide">Mode</th>
                <th className="text-right px-2 py-2 font-bold uppercase text-[10px] tracking-wide">Colli</th>
                <th className="text-right px-2 py-2 font-bold uppercase text-[10px] tracking-wide">Gewicht</th>
                <th className="text-right px-2 py-2 font-bold uppercase text-[10px] tracking-wide">CBM</th>
                <th className="text-center px-2 py-2 font-bold uppercase text-[10px] tracking-wide">Eligible</th>
              </tr>
            </thead>
            <tbody>
              {visibleItems.length === 0 && !loading && (
                <tr>
                  <td colSpan={10} className="text-center py-8 text-gray-400"
                       data-testid="select-shipments-empty">
                    Geen zendingen gevonden.
                  </td>
                </tr>
              )}
              {visibleItems.map((s) => {
                const el = s._eligibility;
                const ModeI = MODE_ICON[el.shipMode] || Package;
                const sd = s.shipment_details || {};
                return (
                  <tr key={s.id}
                       className={`border-b border-gray-100 hover:bg-blue-50/30 ${!el.eligible ? "opacity-60" : ""}`}
                       data-testid={`select-shipment-row-${s.tracking_number}`}>
                    <td className="px-2 py-2">
                      <Checkbox
                        checked={!!selected[s.id]}
                        onCheckedChange={(v) =>
                          setSelected((prev) => ({ ...prev, [s.id]: !!v }))}
                        disabled={!el.eligible}
                        data-testid={`select-shipment-cb-${s.tracking_number}`}
                      />
                    </td>
                    <td className="px-2 py-2 font-mono font-semibold text-[#082c50]">
                      {s.tracking_number}
                    </td>
                    <td className="px-2 py-2">
                      {s.sender?.name || "-"}
                    </td>
                    <td className="px-2 py-2">
                      {s.recipient?.name || "-"}
                    </td>
                    <td className="px-2 py-2 text-gray-600">
                      {s.destination_country_name || s.destination_country_code || sd.destination || "-"}
                    </td>
                    <td className="px-2 py-2">
                      <span className="inline-flex items-center gap-1">
                        <ModeI className="w-3 h-3" />
                        {MODE_LABEL[el.shipMode] || "?"}
                      </span>
                    </td>
                    <td className="px-2 py-2 text-right">{sd.piece_count ?? "-"}</td>
                    <td className="px-2 py-2 text-right">{sd.weight_kg ?? "-"}</td>
                    <td className="px-2 py-2 text-right">{sd.cbm ?? "-"}</td>
                    <td className="px-2 py-2 text-center">
                      {el.eligible ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                          <CheckCircle2 className="w-3 h-3 mr-1" /> OK
                        </Badge>
                      ) : (
                        <div className="inline-flex flex-col items-start gap-0.5">
                          <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200 text-[10px]">
                            <AlertTriangle className="w-3 h-3 mr-1" /> Niet eligible
                          </Badge>
                          <span className="text-[10px] text-red-600">{el.reasons[0]}</span>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <DialogFooter className="border-t border-gray-200 pt-3 flex-shrink-0">
          <div className="flex items-center gap-3 flex-1 text-xs text-gray-600"
                data-testid="select-shipments-totals">
            <div>
              <strong>{selectedCount}</strong> geselecteerd
              {selectedCount > 0 && (
                <> · <strong>{totals.colli}</strong> colli · <strong>{totals.weight.toFixed(1)}</strong> kg · <strong>{totals.cbm.toFixed(3)}</strong> m³</>
              )}
            </div>
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)}
                   className="rounded-none text-xs" data-testid="select-shipments-cancel">
            Annuleren
          </Button>
          <Button onClick={handleSubmit}
                   disabled={submitting || selectedCount === 0}
                   className="rounded-none text-xs bg-[#0055FF] hover:bg-[#0044CC] text-white"
                   data-testid="select-shipments-submit">
            {submitting ? (
              <><Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> Toevoegen...</>
            ) : (
              <>Voeg {selectedCount} zending{selectedCount === 1 ? "" : "en"} toe</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
